import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getLivePulseSnapshot, getNationRivalry } from "@/lib/social.functions";
import { deriveGlobalWorldStatus } from "@/lib/global/globalMatchEventsEngine";
import { useMobileMatchMode } from "@/lib/launchHardening/mobileMatchMode";
import { useVisualQueueSnapshot } from "@/lib/launchHardening/priorityVisualQueue";
import { useShowcaseMode } from "@/lib/showcase/showcaseMode";
import {
  getRecentAlerts,
  snapshotCounters,
  tickWarRoom,
  type SpikeAlert,
} from "@/lib/warRoom/warRoomEngine";

type TenantPulse = { tenant_slug: string; tenant_name: string | null; country_code: string | null; pulses: number };

/**
 * Light, cached war-room snapshot. Reuses the same react-query keys as
 * cinematic/global hooks → zero extra polling pressure.
 * Tenant pulses are cached for 60s.
 */
export function useWarRoomSnapshot() {
  const pulse = useQuery({
    queryKey: ["social", "pulse_snap"],
    queryFn: () => getLivePulseSnapshot(),
    refetchInterval: 15_000,
  });
  const rivalry = useQuery({
    queryKey: ["social", "nation_rivalry"],
    queryFn: () => getNationRivalry(),
    refetchInterval: 20_000,
  });

  const tenants = useQuery({
    queryKey: ["warRoom", "tenant_pulses"],
    queryFn: async (): Promise<TenantPulse[]> => {
      const since = new Date(Date.now() - 1000 * 60 * 60).toISOString();
      const [{ data: pulses }, { data: tcfg }] = await Promise.all([
        supabase
          .from("hype_pulses")
          .select("tenant_id, pulse_count")
          .gte("created_at", since)
          .not("tenant_id", "is", null)
          .limit(2000),
        supabase.from("tenants_config").select("tenant_slug, tenant_name, country_code"),
      ]);
      const agg = new Map<string, number>();
      for (const p of (pulses ?? []) as Array<{ tenant_id: string | null; pulse_count: number | null }>) {
        if (!p.tenant_id) continue;
        agg.set(p.tenant_id, (agg.get(p.tenant_id) ?? 0) + (p.pulse_count ?? 1));
      }
      const tMap = new Map(
        ((tcfg ?? []) as Array<{ tenant_slug: string; tenant_name: string | null; country_code: string | null }>)
          .map((t) => [t.tenant_slug, t]),
      );
      return Array.from(agg.entries())
        .map(([slug, p]) => ({
          tenant_slug: slug,
          tenant_name: tMap.get(slug)?.tenant_name ?? null,
          country_code: tMap.get(slug)?.country_code ?? null,
          pulses: p,
        }))
        .sort((a, b) => b.pulses - a.pulses);
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const mmm = useMobileMatchMode();
  const queue = useVisualQueueSnapshot();
  const sc = useShowcaseMode();

  const world = useMemo(() => {
    return deriveGlobalWorldStatus({
      activeFans: pulse.data?.active_fans ?? 0,
      hypeBurst: pulse.data?.hype_burst ?? 0,
      rivalry: (rivalry.data?.rows ?? []).map((r) => ({
        nation_code: r.nation_code ?? "",
        hype_total: r.hype_total,
      })),
    });
  }, [pulse.data, rivalry.data]);

  // Local tick — feed engine when pulse/rivalry change.
  const [alerts, setAlerts] = useState<SpikeAlert[]>(() => getRecentAlerts());
  useEffect(() => {
    tickWarRoom({
      activeFans: pulse.data?.active_fans ?? 0,
      hypeBurst: pulse.data?.hype_burst ?? 0,
      topNation: world.dominantNation,
      topNationShare:
        world.regionalShares[0]?.share ??
        0,
    });
    setAlerts(getRecentAlerts());
  }, [pulse.data, rivalry.data, world]);

  // Also refresh alert TTL every 20s without re-deriving.
  useEffect(() => {
    const id = setInterval(() => setAlerts(getRecentAlerts()), 20_000);
    return () => clearInterval(id);
  }, []);

  const counters = snapshotCounters();

  return {
    pulse: pulse.data,
    rivalry: rivalry.data?.rows ?? [],
    world,
    tenants: tenants.data ?? [],
    alerts,
    counters,
    mmm,
    queue,
    showcase: sc,
    loading: pulse.isLoading || rivalry.isLoading,
  };
}
